import { TStageGroupId, TStopStageKey } from '@forwardflow/types/onboarding'

/**
 * The three phases a lead goes through with Forward Flow, in the order the
 * screen explains them. Each phase owns one or more buckets (below).
 */
export type TPhaseId = 'work' | 'book' | 'stop'

export interface IPhase {
   id: TPhaseId
   step: number
   title: string
   /** One plain sentence an amateur can read. */
   lede: string
}

export const PHASES: IPhase[] = [
   {
      id: 'work',
      step: 1,
      title: 'We work the lead',
      lede: 'Leads sitting in these stages get calls and texts from Forward Flow until they answer.',
   },
   {
      id: 'book',
      step: 2,
      title: 'They book',
      lede: 'When a lead books an appointment we move them here and hand them to you.',
   },
   {
      id: 'stop',
      step: 3,
      title: 'You take over, we stop',
      lede: 'The moment a lead lands in any of these, Forward Flow never contacts them again.',
   },
]

/** Every group a stage or tag can be mapped to on this screen. */
export type TBucketKey =
   | Extract<
        TStageGroupId,
        'ff-nurturing' | 'ff-appointment-set' | 'ff-appointment-missed'
     >
   | TStopStageKey

export interface IBucket {
   key: TBucketKey
   phase: TPhaseId
   title: string
   /** The question the customer is answering for this bucket. */
   question: string
   /** Optional hint under the question. */
   help?: string
}

export const BUCKETS: IBucket[] = [
   {
      key: 'ff-nurturing',
      phase: 'work',
      title: 'Leads to work',
      question: 'Which stage holds the leads you want Forward Flow to call?',
      help: 'Usually "New Lead" or "Nurture". Add more than one if new leads live in several stages.',
   },
   {
      key: 'ff-appointment-set',
      phase: 'book',
      title: 'Appointment set',
      question: 'Where should a lead go once they book?',
      help: 'We move the lead here. This is your cue to take over.',
   },
   {
      key: 'ff-appointment-missed',
      phase: 'book',
      title: 'Appointment missed',
      question: 'Where should a lead go if they no-show?',
      help: 'Optional. Leads here get a follow-up to rebook.',
   },
   {
      key: 'ff-under-contract',
      phase: 'stop',
      title: 'Under contract',
      question: 'Offer accepted, deal in progress.',
   },
   {
      key: 'ff-active-client',
      phase: 'stop',
      title: 'Active client',
      question: "You're already working with them.",
   },
   {
      key: 'ff-closed-client',
      phase: 'stop',
      title: 'Closed',
      question: 'The deal is done.',
   },
   {
      key: 'ff-do-not-contact',
      phase: 'stop',
      title: 'Do not contact',
      question: 'They asked not to be contacted.',
   },
   {
      key: 'ff-has-agent',
      phase: 'stop',
      title: 'Has another agent',
      question: 'Represented by someone else.',
   },
   {
      key: 'ff-other-stop',
      phase: 'stop',
      title: 'Anything else',
      question: 'Any other stage or tag that should stop us.',
   },
]

/**
 * Buckets that must have at least one stage or tag before onboarding can
 * finish. Mirrors `isMandatory` in `initialStageMappingsGroups`; the live
 * flags on the saved groups win when present (see `isBucketRequired`).
 */
export const REQUIRED_BUCKETS: TBucketKey[] = [
   'ff-nurturing',
   'ff-appointment-set',
   'ff-under-contract',
   'ff-closed-client',
   'ff-do-not-contact',
]
